import React, { useState, useEffect } from 'react';
import { useAppSelector } from '../store/hooks/reduxHooks';
import { User as UserIcon, Mail, Phone, Clock, CheckCircle } from 'lucide-react';
import CleverPeLogo from '/CleverPeLogos/CleverPe_White_H.svg';
import { fetchLenderUserById, type LenderUser } from '../services/lender';

const ProfilePage = () => {
  const { token, user } = useAppSelector(state => state.auth); // 👈 user must contain _id
  const [lenderUser, setLenderUser] = useState<LenderUser | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [logoLoaded, setLogoLoaded] = useState(false);
  // Only profile tab remains

  /** ---------------- FETCH LENDER USER ---------------- */
  useEffect(() => {
    if (!user?._id || !token) {
      setIsLoading(false);
      return;
    }

    const getLenderUserInfo = async () => {
      try {
        const info = await fetchLenderUserById(user._id, token);
        setLenderUser(info);
      } catch (error) {
        console.error('Error fetching lender user info:', error);
      } finally {
        setIsLoading(false);
      }
    };

    getLenderUserInfo();
  }, [user?._id, token]);

  /** ---------------- PRELOAD LOGO ---------------- */
  useEffect(() => {
    const img = new Image();
    img.src = CleverPeLogo;
    img.onload = () => setLogoLoaded(true);
  }, []);

  /** ---------------- STATES ---------------- */
  if (isLoading) {
    return (
      <div className="p-6 flex justify-center">
        <p>Loading profile information...</p>
      </div>
    );
  }

  if (!lenderUser) {
    return (
      <div className="p-6">
        <p className="text-red-500">User information not available</p>
      </div>
    );
  }

  /** ---------------- RENDER ---------------- */
  return (
    <div className="p-5 bg-gray-50">
      <div className="bg-white rounded-lg shadow overflow-hidden max-w-4xl mx-auto">
        
        {/* Header */}
        <div className="relative">
          <div className="h-20 bg-gradient-to-r from-blue-500 to-indigo-600 flex justify-end items-center pr-6">
            {logoLoaded ? (
              <img src={CleverPeLogo} alt="CleverPe Logo" className="h-10" />
            ) : (
              <div className="h-10 w-28 bg-blue-400 animate-pulse rounded" />
            )}
          </div>

          {/* Avatar */}
          <div className="absolute bottom-0 left-8 transform translate-y-1/2">
            <div className="w-20 h-20 rounded-full bg-gradient-to-r from-indigo-500 to-purple-600 flex items-center justify-center text-white text-3xl font-bold border-4 border-white shadow-md">
              {lenderUser.name?.charAt(0).toUpperCase()}
            </div>
          </div>
        </div>

        {/* Header Info */}
        <div className="pt-14 px-8 pb-4">
          <h2 className="text-2xl font-bold text-gray-900">{lenderUser.name}</h2>
          <div className="flex items-center mt-1">
            <span
              className={`inline-block w-2 h-2 rounded-full ${
                lenderUser.isActive ? 'bg-green-600' : 'bg-red-600'
              } mr-1`}
            />
            <span className="text-xs text-gray-500">
              {lenderUser.isActive ? 'Active' : 'Inactive'}
            </span>
          </div>
        </div>

        {/* Only Profile Tab - No tab navigation needed */}

        {/* Profile Content Only */}
        <div className="p-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <h3 className="font-semibold mb-4">Basic Information</h3>
              <InfoRow icon={UserIcon} label="Full Name" value={lenderUser.name} />
              <InfoRow icon={Mail} label="Email Address" value={lenderUser.email ?? 'Not provided'} />
              <InfoRow icon={Phone} label="Phone Number" value={lenderUser.mobileNo} />
            </div>
            
          </div>
        </div>
      </div>
    </div>
  );
};

/** ---------------- SMALL REUSABLE ROW ---------------- */
const InfoRow = ({
  icon: Icon,
  label,
  value,
  valueClass = ''
}: any) => (
  <div className="flex items-start mb-4">
    <Icon className="h-4 w-4 text-gray-400 mt-1" />
    <div className="ml-3">
      <div className="text-xs text-gray-500">{label}</div>
      <div className={`text-sm font-medium ${valueClass}`}>{value}</div>
    </div>
  </div>
);

export default ProfilePage;
